import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, IconButton, Menu, MenuItem, TextField, Typography } from "@mui/material";

import { PaymentLinkRowProps } from "../payment.types";
import { PaymentLinkCopyIcon, PaymentLinksOptionsIcon } from "../../../assets/icons/MUI/PaymentIcons";
import { useFormatLocalizedDate } from "../../../utils/formatDate";

const notLastRowStyle = {
  paddingBottom: "32px",
  marginBottom: "32px",
  borderBottom: "1px solid #E4E7EC",
};

const PaymentLinkRow = ({ Link, isLast, handleOnSharePaymentLink, handleOnEndPaymentLink }: PaymentLinkRowProps) => {
  const {
    id,
    amount,
    currency,
    reasonOfPayment,
    link,
    numberOfPaymentsReceived,
    recipientsEmailOutSideGoalngo,
    expiryDate,
    isActive,
  } = Link;

  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const isExpired = new Date(expiryDate).getTime() < Date.now();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        rowGap: "24px",
        ...(!isLast && notLastRowStyle),
      }}
    >
      <Typography
        sx={{
          color: "var(--Dark---600, #707070)",
          fontFamily: "Montserrat",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 500,
          lineHeight: "24px",
        }}
      >
        {useFormatLocalizedDate(expiryDate)}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "12px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: "12px",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "2px" }}>
            <Box sx={{ display: "flex", columnGap: "80px" }}>
              <Typography
                sx={{
                  color: "var(--Gray-700, #344054)",
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: "20px",
                }}
              >
                {`${amount / 100} ${currency.toUpperCase()}`}
              </Typography>

              {isExpired ||
                (!isActive && (
                  <Typography
                    component="span"
                    sx={{
                      color: "var(--Error-700, #B42318)",
                      textAlign: "center",
                      fontFamily: "Montserrat",
                      fontSize: "12px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "18px",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      background: "var(--Error-50, #FEF3F2)",
                    }}
                  >
                    {t("pages.payment.paymentLinks.linkExpired")}
                  </Typography>
                ))}
            </Box>
            <Typography
              sx={{
                color: "#96A3B6",
                fontFamily: "Montserrat",
                fontSize: "12px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "18px",
                maxWidth: "365px",
              }}
            >
              {t(`pages.payment.paymentLinks.${reasonOfPayment}`)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", columnGap: "12px" }}>
            <CopyableTextField link={link} disabled={isExpired || !isActive} />
            <IconButton
              id="menu-button"
              aria-controls={open ? "menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              sx={{
                borderRadius: "8px",
                border: "1px solid #D0D5DD",
                background: "var(--Base-White, #FFF)",
                boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                padding: "8px",
              }}
              disableRipple
              disabled={isExpired || !isActive}
            >
              <PaymentLinksOptionsIcon />
            </IconButton>
            <Menu id="menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
              {/* <MenuItem onClick={() => handleOnSharePaymentLink(Link)}>
                {t("pages.payment.paymentLinks.sharePaymentLink")}
              </MenuItem> */}
              <MenuItem onClick={() => handleOnEndPaymentLink(Link)}>
                {t("pages.payment.paymentLinks.endPaymentLink")}
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        <Box sx={{ display: "flex", columnGap: "4px" }}>
          <Typography
            sx={{
              color: "var(--Gray-700, #344054)",
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "20px",
            }}
          >
            {t("pages.payment.paymentLinks.numberOfPaymentReceived")}
          </Typography>
          <Chip
            label={numberOfPaymentsReceived.succeeded || 0}
            sx={{
              borderRadius: "3px",
              background: "var(--Gray-100, #F2F4F7)",
              color: "var(--Gray-700, #344054)",
              textAlign: "center",
              fontFamily: "Montserrat",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "18px",
              width: "fit-content",
              height: "fit-content",
              "& .MuiChip-label": {
                padding: "0 4px",
              },
            }}
          />
        </Box>
        {recipientsEmailOutSideGoalngo && recipientsEmailOutSideGoalngo.length > 0 && (
          <Box sx={{ display: "flex", gap: "4px" }}>
            {recipientsEmailOutSideGoalngo.map(
              (email: string, idx: number) =>
                idx < 3 && (
                  <Chip
                    variant="outlined"
                    label={email}
                    key={idx}
                    sx={{
                      borderRadius: "15px",
                      border: "1px solid var(--Gray---200, #CFCFCF)",
                      background: "var(--Base-White, #FFF)",
                      color: "var(--Dark---800, #414141)",
                      textAlign: "center",
                      fontFamily: "Montserrat",
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "20px",
                      "& .MuiChip-label": {
                        padding: "4px 10px",
                      },
                    }}
                  />
                )
            )}
            {recipientsEmailOutSideGoalngo.length > 3 && (
              <Chip
                variant="outlined"
                label={`+${recipientsEmailOutSideGoalngo.length - 3}`}
                sx={{
                  borderRadius: "15px",
                  border: "1px solid var(--Gray---200, #CFCFCF)",
                  background: "#F2F4F7",
                  color: "var(--Dark---800, #414141)",
                  textAlign: "center",
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "20px",
                  "& .MuiChip-label": {
                    padding: "4px 10px",
                  },
                }}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PaymentLinkRow;

const CopyableTextField = ({ link, disabled }: { link: string; disabled: boolean }) => {
  const handleCopy = () => navigator.clipboard.writeText(link);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        columnGap: "2px",
        width: "350px",
        padding: "0 2px 0 4px",
        borderRadius: "8px",
        border: "1px solid var(--Gray-300, #D0D5DD)",
        background: "var(--Base-White, #FFF)",
        boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
      }}
    >
      <TextField
        fullWidth
        value={link}
        disabled={disabled}
        sx={{
          "& .MuiInputBase-root.MuiOutlinedInput-root, & .MuiOutlinedInput-notchedOutline, & .MuiInputBase-input": {
            border: "none !important",
            padding: "0 !important",
          },
        }}
      />
      <IconButton onClick={handleCopy} sx={{ padding: "4px" }} disabled={disabled}>
        <PaymentLinkCopyIcon />
      </IconButton>
    </Box>
  );
};
